/*
  Behaviour for the installed-PWA bottom bar (snippets/pwa-bottom-bar.liquid).

  Loaded with `defer` after constants.js / pubsub.js / global.js / cart-drawer.js,
  so `subscribe`, `PUB_SUB_EVENTS`, <header-drawer> and <cart-drawer> are all
  available by the time this runs.
*/
(function () {
  const bar = document.getElementById('PwaBar');
  if (!bar) return;

  const MENU_DETAILS_ID = 'Details-menu-drawer-container';
  const STATE_KEY = 'pwaIndex';
  const SESSION_MAX = 'pwaNavMax';
  const SESSION_LAST = 'pwaNavLast';

  const backBtn = bar.querySelector('[data-pwa-action="back"]');
  const forwardBtn = bar.querySelector('[data-pwa-action="forward"]');
  const menuBtn = bar.querySelector('[data-pwa-action="menu"]');
  const cartBtn = bar.querySelector('.pwa-bar__btn--cart');
  const badge = bar.querySelector('[data-pwa-cart-count]');
  const badgeLabel = bar.querySelector('[data-pwa-cart-label]');

  /* ------------------------------------------------------------- history --

     There is no API for "can I go forward?", so stamp every history entry we
     create with an incrementing index and remember the highest one reached in
     this session. Back is dead at index 0, forward at the newest index.

     Theme code (facets, for one) pushes its own entries without our stamp; when
     we land on one of those we stop guessing and leave both buttons enabled.
     The buttons always call history.back()/forward() regardless, so the worst
     case is a dimmed state that is briefly out of date.
  */
  let navIndex = 0;
  let navMax = 0;
  let navUnknown = false;

  function readSession(key, fallback) {
    try {
      const value = sessionStorage.getItem(key);
      return value === null ? fallback : parseInt(value, 10);
    } catch (error) {
      return fallback;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, String(value));
    } catch (error) {
      /* private mode — fall back to always-enabled buttons */
    }
  }

  function syncHistoryPosition() {
    const state = history.state;
    const stamped = state && typeof state[STATE_KEY] === 'number' ? state[STATE_KEY] : null;

    navUnknown = false;
    navMax = readSession(SESSION_MAX, 0);

    if (stamped === null) {
      // A fresh forward navigation: this entry becomes the newest one.
      navIndex = readSession(SESSION_LAST, -1) + 1;
      navMax = navIndex;
      try {
        const next = Object.assign({}, state);
        next[STATE_KEY] = navIndex;
        history.replaceState(next, '');
      } catch (error) {
        navUnknown = true;
      }
    } else {
      navIndex = stamped;
      if (navIndex > navMax) navMax = navIndex;
    }

    writeSession(SESSION_MAX, navMax);
    writeSession(SESSION_LAST, navIndex);
  }

  function setDisabled(button, disabled) {
    if (!button) return;
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  function renderHistoryButtons() {
    setDisabled(backBtn, !navUnknown && navIndex <= 0);
    setDisabled(forwardBtn, !navUnknown && navIndex >= navMax);
  }

  syncHistoryPosition();
  renderHistoryButtons();

  window.addEventListener('popstate', function () {
    const state = history.state;
    if (state && typeof state[STATE_KEY] === 'number') {
      navIndex = state[STATE_KEY];
      navUnknown = false;
      writeSession(SESSION_LAST, navIndex);
    } else {
      navUnknown = true;
    }
    renderHistoryButtons();
  });

  /* ---------------------------------------------------------------- menu -- */

  function menuDetails() {
    return document.getElementById(MENU_DETAILS_ID);
  }

  function renderMenuButton() {
    if (!menuBtn) return;
    const details = menuDetails();
    const isOpen = !!details && details.classList.contains('menu-opening');
    menuBtn.classList.toggle('is-active', isOpen);
    menuBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  const details = menuDetails();
  if (!details) {
    // No menu assigned to the header, so nothing to open.
    setDisabled(menuBtn, true);
  } else {
    renderMenuButton();
    new MutationObserver(renderMenuButton).observe(details, {
      attributes: true,
      attributeFilter: ['class', 'open'],
    });
  }

  /* ---------------------------------------------------------------- cart -- */

  function setCartCount(count) {
    if (!badge) return;
    const value = typeof count === 'number' && count > 0 ? count : 0;
    badge.textContent = value > 99 ? '99+' : String(value);
    badge.hidden = value === 0;
    if (badgeLabel) {
      badgeLabel.textContent = value === 1 ? '1 item' : value + ' items';
    }
  }

  function refreshCartCount() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(cartUrl + '.js')
      .then((response) => response.json())
      .then((cart) => setCartCount(cart.item_count))
      .catch(() => {
        /* offline or blocked — leave the server-rendered count in place */
      });
  }

  if (cartBtn && document.querySelector('cart-drawer')) {
    cartBtn.setAttribute('aria-haspopup', 'dialog');
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      const count = event && event.cartData ? event.cartData.item_count : undefined;
      // /cart/add.js responses describe the added line, not the whole cart
      if (typeof count === 'number') {
        setCartCount(count);
      } else {
        refreshCartCount();
      }
    });
  }

  /* ------------------------------------------------------------- actions -- */

  bar.addEventListener('click', function (event) {
    const button = event.target.closest('[data-pwa-action]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    switch (button.dataset.pwaAction) {
      case 'back':
        event.preventDefault();
        history.back();
        break;

      case 'forward':
        event.preventDefault();
        history.forward();
        break;

      case 'menu': {
        event.preventDefault();
        // Let the theme's own state machine run: MenuDrawer.onSummaryClick
        // toggles both ways, and openMenuDrawer() alone never sets [open].
        const summary = document.querySelector('#' + MENU_DETAILS_ID + ' > summary');
        if (summary) summary.click();
        break;
      }

      case 'cart': {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          event.preventDefault();
          drawer.open(cartBtn);
        }
        break;
      }
    }
  });

  /* --------------------------------------------------------------- bfcache */

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    syncHistoryPosition();
    renderHistoryButtons();
    renderMenuButton();
    refreshCartCount();
  });
})();
